import { Icon } from '@openeducation/oe-rn-components';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Animated, Easing, StyleSheet, View } from 'react-native';

import { useColors } from '../../../../hooks';
import ThemedText2 from '../../../themed-text-2';

interface TabErrorTooltipProps {
  isVisible?: boolean;
  children?: React.ReactNode;
}

const TabErrorTooltip: React.FC<TabErrorTooltipProps> = ({
  children,
  isVisible = false,
}) => {
  const { t } = useTranslation();
  const colors = useColors();
  const [tooltipWidth, setTooltipWidth] = useState(0);
  const tooltipAnimation = useRef(new Animated.Value(0));
  const tooltipTimeoutId = useRef<NodeJS.Timeout>();

  useEffect(() => {
    function animateTooltip(toValue: 1 | 0) {
      Animated.timing(tooltipAnimation.current, {
        toValue,
        duration: 800,
        delay: 200,
        easing: Easing.in(Easing.elastic(0.5)),
        useNativeDriver: true,
      }).start();
    }

    animateTooltip(isVisible ? 1 : 0);

    if (isVisible) {
      tooltipTimeoutId.current = setTimeout(() => {
        animateTooltip(0);
      }, 5000);
    }

    return () => {
      if (tooltipTimeoutId.current) {
        clearTimeout(tooltipTimeoutId.current);
      }
    };
  }, [isVisible]);

  const translateX = tooltipAnimation.current.interpolate({
    inputRange: [0, 1],
    outputRange: [0, -(tooltipWidth + 24)],
    extrapolate: 'clamp',
  });

  const opacity = tooltipAnimation.current.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 1],
    extrapolate: 'clamp',
  });

  return (
    <View>
      <View style={styles.container}>
        <Animated.View
          onLayout={(e) => setTooltipWidth(e.nativeEvent.layout.width)}
          style={[
            styles.innerContainer,
            { backgroundColor: colors.gray },
            { transform: [{ translateX }], opacity },
          ]}
        >
          <View style={[styles.arrow, { backgroundColor: colors.gray }]} />
          <ThemedText2 style={styles.text} type="onPrimary">
            {t('lp.errors.clickToHear')}
          </ThemedText2>
          <Icon name="hand-point-right" color="white" size={18} type="solid" />
        </Animated.View>
      </View>
      {children}
    </View>
  );
};

export default TabErrorTooltip;

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  innerContainer: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    flexDirection: 'row',
    left: 0,
    borderRadius: 4,
    alignItems: 'center',
  },
  arrow: {
    position: 'absolute',
    width: 20,
    height: 20,
    borderRadius: 4,
    right: -6,
    zIndex: -1,
    transform: [{ rotate: '45deg' }],
  },
  text: {
    fontSize: 14,
    marginRight: 12,
  },
});
